import type { Alpha3Code } from "i18n-iso-countries"
import { EN_COUNTRY_LABELS } from "./country-labels"
import { isNilOrEmpty } from "./string"

export type CountryList = readonly Alpha3Code[]

export const USA_COUNTRY_CODE = "USA"
export const CANADA_COUNTRY_CODE = "CAN"
export const AUS_COUNTRY_CODE = "AUS"
export const UK_COUNTRY_CODE = "GBR"
export const WITHDRAWAL_ONLY_COUNTRIES = {
  BULGARIA: "BGR",
  KENYA: "KEN",
  MONACO: "MCO",
  NAMIBIA: "NAM",
  SOUTH_AFRICA: "ZAF",
} as const

// NOTE: ordered by full country name except USA
// this originates in twapi-ember/addon/models/address.ts
export const ORDERED_ENTITY_OWNER_PLUS_WITHDRAWAL_ONLY_COUNTRIES: CountryList =
  [
    USA_COUNTRY_CODE,
    "AND",
    "ARG",
    "AUS",
    "AUT",
    "BHR",
    "BEL",
    WITHDRAWAL_ONLY_COUNTRIES.BULGARIA,
    "BRA",
    "BRN",
    CANADA_COUNTRY_CODE,
    "CHL",
    "COL",
    "CZE",
    "DNK",
    "DOM",
    "ECU",
    "EGY",
    "EST",
    "FIN",
    "FRA",
    "PYF",
    "DEU",
    "GRC",
    "GGY",
    "HUN",
    "ISL",
    "IND",
    "IDN",
    "IRL",
    "IMN",
    "ISR",
    "ITA",
    "JEY",
    WITHDRAWAL_ONLY_COUNTRIES.KENYA,
    "KOR",
    "KWT",
    "LIE",
    "LTU",
    "LUX",
    "MYS",
    "MEX",
    WITHDRAWAL_ONLY_COUNTRIES.MONACO,
    WITHDRAWAL_ONLY_COUNTRIES.NAMIBIA,
    "NLD",
    "NZL",
    "NOR",
    "OMN",
    "PER",
    "PHL",
    "POL",
    "PRT",
    "ROU",
    "SMR",
    "SAU",
    "SGP",
    "SVK",
    "SVN",
    WITHDRAWAL_ONLY_COUNTRIES.SOUTH_AFRICA,
    "ESP",
    "SWE",
    "CHE",
    "TWN",
    "THA",
    "ARE",
    "GBR",
    "URY",
  ]

// NOTE: [KT] Blocked jurisdictions aren't allowed for foreign account citizenship but are allowed for visa holders and permanent residents
const APEX_RESTRICTED_COUNTRIES_OPTIONS: CountryList = [
  "AFG",
  "ALB",
  "AGO",
  "BDI",
  "BFA",
  WITHDRAWAL_ONLY_COUNTRIES.BULGARIA,
  "BIH",
  "BLR",
  "BOL",
  "BRB",
  "BUR" as Alpha3Code,
  "BWA",
  "CAF",
  "CIV",
  "CMR",
  "COD",
  "CUB",
  "CYM",
  "DZA",
  "ETH",
  "GIB",
  "GHA",
  "GUY",
  "HRV",
  "HTI",
  "IRN",
  "IRQ",
  "JOR",
  "KEN",
  "KHM",
  "LAO",
  "LBN",
  "LBY",
  "LKA",
  "LVA",
  "MAR",
  WITHDRAWAL_ONLY_COUNTRIES.MONACO,
  "MKD",
  "MLI",
  "MLT",
  "MMR",
  "MOZ",
  "MUS",
  "NAM",
  "NGA",
  "NIC",
  "NPL",
  "PAK",
  "PAN",
  "PRK",
  "RUS",
  "SDN",
  "SEN",
  "SOM",
  "SSD",
  "STP",
  "SYR",
  "TCD",
  "TUR",
  "TZA",
  "UGA",
  "UKR",
  "VEN",
  "VGB",
  "VNM",
  "VUT",
  "YEM",
  "XKX",
  WITHDRAWAL_ONLY_COUNTRIES.SOUTH_AFRICA,
  "ZWE",
]

const BROKER_RESTRICTED_COUNTRIES_OPTIONS: CountryList = [
  "AGO",
  "CIV",
  "COD",
  "COM",
  "ERI",
  "ESH",
  "GIN",
  "GNQ",
  "HTI",
  "LBR",
  "MRT",
  "MSR",
  "NER",
  "NGA",
  "PSE",
  "RUS",
  "SLE",
  "TJK",
  "TKM",
  "VEN",
]

export const BROKER_HEIGHTENED_RESTRICTION_COUNTRIES_OPTIONS = ["RUS"]

export const RESTRICTED_COUNTRIES_OPTIONS = new Set<Alpha3Code>([
  ...APEX_RESTRICTED_COUNTRIES_OPTIONS,
  ...BROKER_RESTRICTED_COUNTRIES_OPTIONS,
])

export const ENTITY_OWNER_COUNTRIES_OPTIONS =
  ORDERED_ENTITY_OWNER_PLUS_WITHDRAWAL_ONLY_COUNTRIES.filter(
    (country) =>
      !Object.values<string>(WITHDRAWAL_ONLY_COUNTRIES).includes(country)
  )

/**
 * A list of all supported countries excluding Canada. Used as the list of
 * countries that we support on sign-up.
 */
export const ACCEPTED_COUNTRIES_OPTIONS = ENTITY_OWNER_COUNTRIES_OPTIONS.filter(
  (country) => country !== CANADA_COUNTRY_CODE
)

/**
 * A list of all supported countries excluding the USA and Canada. Used as the list of
 * countries that we support as an alternate mailing address on sign-up (when the mailing
 * address is not in the USA).
 */
export const FOREIGN_COUNTRIES_OPTIONS = ACCEPTED_COUNTRIES_OPTIONS.filter(
  (country) => country !== USA_COUNTRY_CODE
)

export const WITHDRAWAL_ORDERED_COUNTRIES_OPTIONS =
  ORDERED_ENTITY_OWNER_PLUS_WITHDRAWAL_ONLY_COUNTRIES.filter(
    (country) => country !== USA_COUNTRY_CODE && country !== CANADA_COUNTRY_CODE
  )

// all 249 countries (including territories)
export const ALL_COUNTRIES = Object.keys(EN_COUNTRY_LABELS) as Alpha3Code[]

/**
 * All countries excluding restricted countries
 */
export const ALL_COUNTRIES_EXCLUDING_RESTRICTED = ALL_COUNTRIES.filter(
  (country) => !RESTRICTED_COUNTRIES_OPTIONS.has(country)
)

// FIXME: can we make this work with i18n?
export function getCountryLabel(key: Alpha3Code) {
  return EN_COUNTRY_LABELS[key]
}

export function sortCountriesByLabel(countries: Alpha3Code[]) {
  return countries
    .map(getCountryLabel)
    .sort((a, b) => a.localeCompare(b))
    .map(
      (label) => ALL_COUNTRIES.find((k) => EN_COUNTRY_LABELS[k] === label) ?? ""
    )
    .filter((v) => !isNilOrEmpty(v)) as Alpha3Code[]
}

export function pushElementToFront<T>(arr: T[], element: T) {
  return [element].concat(arr.filter((e) => e !== element))
}

/**
 * A list of all supported countries excluding countries with their own registrations (i.e. USA/AUS).
 * This can be used for an address change when the user is not a US/AU resident. This is relevant to
 * the address section in CAM.
 */
export const FOREIGN_ADDRESS_COUNTRIES: CountryList = [
  ...sortCountriesByLabel(
    Array.from(
      new Set([
        ...ORDERED_ENTITY_OWNER_PLUS_WITHDRAWAL_ONLY_COUNTRIES.filter(
          (country) =>
            country !== USA_COUNTRY_CODE && country !== AUS_COUNTRY_CODE
        ),
      ])
    )
  ),
]

/**
 * A list of ALL countries excluding the USA and restricted countries. This can be used for changing
 * a user's trusted contact when they are not a US resident. This is relevant to the trusted contact
 * address in CAM.
 */
export const FOREIGN_TRUSTED_CONTACT_COUNTRIES_OF_RESIDENCE: CountryList = [
  ...ALL_COUNTRIES_EXCLUDING_RESTRICTED.filter(
    (country) => country !== USA_COUNTRY_CODE
  ),
]

/**
 * A list of ALL countries excluding the restricted countries (with the US at the top of the list). This can be used for
 * the application journey when creating a trusted user. This is relevant to trusted contact address in AO.
 */
export const TRUSTED_CONTACT_COUNTRIES_OF_RESIDENCE: CountryList = [
  USA_COUNTRY_CODE,
  ...sortCountriesByLabel(
    Array.from(new Set([...FOREIGN_TRUSTED_CONTACT_COUNTRIES_OF_RESIDENCE]))
  ),
]

export const CITIZENSHIP_COUNTRY_OPTIONS: CountryList = [
  "AFG",
  "ALB",
  "DZA",
  "ASM",
  "AND",
  "AGO",
  "AIA",
  "ATA",
  "ATG",
  "ARG",
  "ARM",
  "ABW",
  "AUS",
  "AUT",
  "AZE",
  "BHS",
  "BHR",
  "BGD",
  "BRB",
  "BLR",
  "BEL",
  "BLZ",
  "BEN",
  "BMU",
  "BTN",
  "BOL",
  "BES",
  "BIH",
  "BWA",
  "BVT",
  "BRA",
  "IOT",
  "BRN",
  "BGR",
  "BFA",
  "BDI",
  "CPV",
  "KHM",
  "CMR",
  "CAN",
  "CYM",
  "CAF",
  "TCD",
  "CHL",
  "CHN",
  "CXR",
  "CCK",
  "COL",
  "COM",
  "COD",
  "COG",
  "COK",
  "CRI",
  "HRV",
  "CUB",
  "CUW",
  "CYP",
  "CZE",
  "CIV",
  "DNK",
  "DJI",
  "DMA",
  "DOM",
  "ECU",
  "EGY",
  "SLV",
  "GNQ",
  "ERI",
  "EST",
  "ETH",
  "FLK",
  "FRO",
  "FJI",
  "FIN",
  "FRA",
  "GUF",
  "PYF",
  "ATF",
  "GAB",
  "GMB",
  "GEO",
  "DEU",
  "GHA",
  "GIB",
  "GRC",
  "GRL",
  "GRD",
  "GLP",
  "GUM",
  "GTM",
  "GGY",
  "GIN",
  "GNB",
  "GUY",
  "HTI",
  "HMD",
  "VAT",
  "HND",
  "HKG",
  "HUN",
  "ISL",
  "IND",
  "IDN",
  "IRN",
  "IRQ",
  "IRL",
  "IMN",
  "ISR",
  "ITA",
  "JAM",
  "JPN",
  "JEY",
  "JOR",
  "KAZ",
  "KEN",
  "KIR",
  "PRK",
  "KOR",
  "KWT",
  "KGZ",
  "LAO",
  "LVA",
  "LBN",
  "LSO",
  "LBR",
  "LBY",
  "LIE",
  "LTU",
  "LUX",
  "MAC",
  "MKD",
  "MDG",
  "MWI",
  "MYS",
  "MDV",
  "MLI",
  "MLT",
  "MHL",
  "MTQ",
  "MRT",
  "MUS",
  "MYT",
  "MEX",
  "FSM",
  "MDA",
  "MCO",
  "MNG",
  "MNE",
  "MSR",
  "MAR",
  "MOZ",
  "MMR",
  "NAM",
  "NRU",
  "NPL",
  "NLD",
  "NCL",
  "NZL",
  "NIC",
  "NER",
  "NGA",
  "NIU",
  "NFK",
  "MNP",
  "NOR",
  "OMN",
  "PAK",
  "PLW",
  "PSE",
  "PAN",
  "PNG",
  "PRY",
  "PER",
  "PHL",
  "PCN",
  "POL",
  "PRT",
  "QAT",
  "ROU",
  "RUS",
  "RWA",
  "REU",
  "BLM",
  "SHN",
  "KNA",
  "LCA",
  "MAF",
  "SPM",
  "VCT",
  "WSM",
  "SMR",
  "STP",
  "SAU",
  "SEN",
  "SRB",
  "SYC",
  "SLE",
  "SGP",
  "SXM",
  "SVK",
  "SVN",
  "SLB",
  "SOM",
  "ZAF",
  "SGS",
  "SSD",
  "ESP",
  "LKA",
  "SDN",
  "SUR",
  "SJM",
  "SWZ",
  "SWE",
  "CHE",
  "SYR",
  "TWN",
  "TJK",
  "TZA",
  "THA",
  "TLS",
  "TGO",
  "TKL",
  "TON",
  "TTO",
  "TUN",
  "TUR",
  "TKM",
  "TCA",
  "TUV",
  "UGA",
  "UKR",
  "ARE",
  "GBR",
  "UMI",
  "USA",
  "URY",
  "UZB",
  "VUT",
  "VEN",
  "VNM",
  "VGB",
  "VIR",
  "WLF",
  "ESH",
  "YEM",
  "ZMB",
  "ZWE",
  "ALA",
]
